import { createBrowserRouter } from 'react-router-dom';

import { AuthChangeRedirector } from '../allauth_auth/routing';
import { HomeRedirect } from '../components/HomeRedirect';
import ErrorPage from '../error-page';
import { registerAppNavigate } from '../lib/navigation/appNavigate';
import {
  BOOKING_ROUTE_PATHS,
  isBookingHost,
} from '../lib/scheduling/bookingHosts';
import {
  BookingPage,
  CancelBookingPage,
  RescheduleBookingPage,
} from '../pages/public/booking';
import Root from '../pages/Root';
import { SharedConversationPage } from '../pages/share';
import { authRoutes } from './auth.routes';
import { bookingRoutes } from './booking.routes';
import { dashboardRoutes } from './dashboard.routes';
import { getStartedRoutes } from './get-started.routes';
import { joinRoutes } from './join.routes';
import { teamsRoutes } from './teams.routes';

export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter([
    // Public share link route (no auth required)
    {
      path: '/s/:token',
      element: <SharedConversationPage />,
      errorElement: <ErrorPage />,
    },
    // Public booking routes (no auth required)
    {
      path: BOOKING_ROUTE_PATHS.shortCodeMeeting,
      element: <BookingPage />,
      errorElement: <ErrorPage />,
    },
    {
      path: BOOKING_ROUTE_PATHS.bookBySlug,
      element: <BookingPage />,
      errorElement: <ErrorPage />,
    },
    {
      path: BOOKING_ROUTE_PATHS.scheduleBySlug,
      element: <BookingPage />,
      errorElement: <ErrorPage />,
    },
    // Booking management routes (no auth required - uses token)
    {
      path: BOOKING_ROUTE_PATHS.cancelBooking,
      element: <CancelBookingPage />,
      errorElement: <ErrorPage />,
    },
    {
      path: BOOKING_ROUTE_PATHS.rescheduleBooking,
      element: <RescheduleBookingPage />,
      errorElement: <ErrorPage />,
    },
    {
      path: '/',
      element: (
        <AuthChangeRedirector>
          <Root />
        </AuthChangeRedirector>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/',
          element: <HomeRedirect />,
        },
        ...authRoutes,
        ...getStartedRoutes,
        ...joinRoutes,
        ...dashboardRoutes,
        ...teamsRoutes,
      ],
    },
  ]);
}

export function createBookingRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(bookingRoutes);
}

export function createRouter(): ReturnType<typeof createBrowserRouter> {
  const router = isBookingHost() ? createBookingRouter() : createAppRouter();
  // Expose the live router's imperative `navigate()` to callers outside the
  // React tree (e.g. toast `onClick` handlers rendered by `sonner`) through
  // the zero-dependency registry — those callers must not import this module
  // directly, since it pulls the entire route tree into their module graph.
  // See lib/navigation/appNavigate.ts.
  registerAppNavigate(path => void router.navigate(path));
  return router;
}
